/**
 * What Promote or Stop changes on blueprints beyond this protocol's own replies, listed before the
 * action runs: a blueprint that starts serving, templates that flip, and write steps that start or
 * stop asking a supervisor. Shown in the Promote review and the Stop confirm.
 */

import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { counted } from '@/modules/back-testing/format';
import { hasRevocation, impactLines, linkTemplatesHref } from './impact-lines';
import type { DraftAddition, PromotionPreview, UnlinkedTemplates } from './types';

interface ImpactListProps {
	preview: PromotionPreview | null | undefined;
	loading: boolean;
	error: boolean;
}

export const ImpactList = ({ preview, loading, error }: ImpactListProps) => {
	if (loading) {
		return (
			<p className='flex items-center gap-1.5 text-[12px] text-gray-800' data-testid='impact-loading'>
				<Loader2 className='size-3 animate-spin' aria-hidden />
				Checking what else this changes…
			</p>
		);
	}
	if (error || !preview) {
		return (
			<p className='text-[12px] text-destructive' data-testid='impact-error'>
				Could not check what else this changes. The action still goes through if you continue.
			</p>
		);
	}
	if (!preview.blueprints.length) {
		return (
			<p className='text-[12px] text-gray-800' data-testid='impact-empty'>
				Nothing else changes.
			</p>
		);
	}
	return (
		<ul className='flex flex-col gap-1.5' data-testid='impact-list'>
			{preview.blueprints.map((impact) => (
				<li key={impact.blueprintId} className='rounded-md border border-gray-300 px-2 py-1.5 text-[12px]'>
					<p className='flex items-center gap-1.5 font-medium'>
						{hasRevocation(impact) && <AlertTriangle className='size-3.5 shrink-0 text-amber-700' aria-hidden />}
						<code>{impact.intent}</code>
					</p>
					<ul className='list-disc pl-5 text-gray-900'>
						{impactLines(impact).map((line) => (
							<li key={line}>{line}</li>
						))}
					</ul>
				</li>
			))}
		</ul>
	);
};

/**
 * The target blueprint holds templates linked to no scenario type. Generate and Promote see a type as
 * covered only through that link, so one of them may already answer this draft's type.
 */
const UnlinkedWarning = ({
	unlinked,
	typeName,
	blueprintId
}: {
	unlinked: UnlinkedTemplates;
	typeName: string;
	blueprintId: string;
}) => {
	const more = unlinked.count - unlinked.samples.length;
	return (
		<div
			className='flex flex-col gap-1 rounded-md bg-amber-50 px-2 py-1.5 text-[12px] text-amber-700'
			role='status'
			data-testid='unlinked-warning'
		>
			<p className='flex items-start gap-1.5'>
				<AlertTriangle className='mt-0.5 size-3.5 shrink-0' aria-hidden />
				<span>
					<span className='font-medium'>
						This blueprint has {counted(unlinked.count, 'template')} not linked to any scenario type.
					</span>{' '}
					One may already answer “{typeName}”. Promoting adds a second one beside it.
				</span>
			</p>
			<ul className='list-disc pl-8'>
				{unlinked.samples.map((sample) => (
					<li key={sample.index}>
						“{sample.text}”{!sample.enabled && ' (off)'}
					</li>
				))}
			</ul>
			{more > 0 && <p className='pl-5'>and {more} more.</p>}
			<a
				href={linkTemplatesHref(blueprintId)}
				target='_blank'
				rel='noreferrer'
				className='flex items-center gap-1 pl-5 font-medium text-indigo-700 hover:text-indigo-900'
			>
				Link them to scenario types in Promoted Protocols
				<ExternalLink className='size-3' aria-hidden />
			</a>
		</div>
	);
};

interface AdditionListProps {
	additions: DraftAddition[];
	loading: boolean;
}

/** The drafted templates Promote adds, one row per intent: where it lands and what comes with it. */
export const AdditionList = ({ additions, loading }: AdditionListProps) => {
	if (loading) {
		return (
			<p className='flex items-center gap-1.5 text-[12px] text-gray-800' data-testid='additions-loading'>
				<Loader2 className='size-3 animate-spin' aria-hidden />
				Checking what Promote adds…
			</p>
		);
	}
	return (
		<ul className='flex flex-col gap-1.5' data-testid='addition-list'>
			{additions.map((addition) => (
				<li
					key={`${addition.draftId}:${addition.intent}`}
					className='flex flex-col gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-[12px]'
				>
					<p className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
						<code className='font-medium'>{addition.intent}</code>
						<span className='text-gray-800'>
							{addition.newBlueprint ? 'starts a new blueprint' : 'adds to the existing blueprint'}
						</span>
						<span className='text-[11px] text-gray-800'>
							draft of {new Date(addition.generatedAt).toLocaleString()} (latest)
						</span>
					</p>
					<p className='text-gray-900'>
						<span className='font-medium'>{addition.typeName}</span> ·{' '}
						{addition.reply === 'auto' ? 'sends by itself' : 'serves as a draft for a person'}
					</p>
					<p className='line-clamp-2 rounded bg-gray-50 px-1.5 py-1 whitespace-pre-wrap text-gray-900'>
						{addition.template}
					</p>
					<p className='text-gray-800'>
						{addition.addedTools.length
							? `New steps (${counted(addition.addedTools.length, 'call')}): ${addition.addedTools.join(', ')}`
							: 'No new steps: the blueprint already makes these calls.'}
					</p>
					<p className='text-gray-800'>+ 1 policy section for when this template applies.</p>
					{addition.blueprintId && (addition.unlinkedTemplates?.count ?? 0) > 0 && (
						<UnlinkedWarning
							unlinked={addition.unlinkedTemplates!}
							typeName={addition.typeName}
							blueprintId={addition.blueprintId}
						/>
					)}
				</li>
			))}
		</ul>
	);
};
